import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { useLang, tx } from '../i18n/LanguageContext';
import { useCategories } from '../hooks/useCategories';

// value: array of category slugs (strings)
// onChange: (newArr) => void
const MultiCategorySelect = ({ value = [], onChange, dataTestId }) => {
  const { lang } = useLang();
  const { categories } = useCategories();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = Array.isArray(value) ? value : [];

  const toggle = (slug) => {
    if (selected.includes(slug)) {
      onChange(selected.filter(s => s !== slug));
    } else {
      onChange([...selected, slug]);
    }
  };

  const remove = (e, slug) => {
    e.stopPropagation();
    onChange(selected.filter(s => s !== slug));
  };

  const labelOf = (slug) => {
    const c = categories.find(x => x.value === slug);
    if (!c) return slug;
    return `${c.icon} ${lang === 'ar' ? c.ar : c.en}`;
  };

  return (
    <div ref={ref} className="relative" data-testid={dataTestId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full bg-slate-50 border border-slate-200 text-slate-900 min-h-[44px] rounded-md px-3 outline-none focus:border-blue-500 hover:border-blue-700 flex items-center gap-2 flex-wrap py-1.5 text-start"
        data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}
      >
        {selected.length === 0 ? (
          <span className="text-slate-500 text-sm">
            {tx(lang, 'اختر تصنيفاً واحداً أو أكثر...', 'Select one or more categories...')}
          </span>
        ) : (
          selected.map(slug => (
            <span key={slug} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 border border-blue-200 text-blue-700 text-xs font-bold">
              {labelOf(slug)}
              <span
                onClick={(e) => remove(e, slug)}
                className="hover:bg-red-500/30 rounded-full p-0.5 cursor-pointer"
              >
                <X className="w-3 h-3" />
              </span>
            </span>
          ))
        )}
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ms-auto ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-blue-200 rounded-md shadow-2xl max-h-72 overflow-y-auto py-1">
          {categories.map(c => {
            const isSel = selected.includes(c.value);
            return (
              <button
                key={c.value}
                type="button"
                onClick={() => toggle(c.value)}
                data-testid={`${dataTestId || 'multicat'}-opt-${c.value}`}
                className={`w-full text-start px-3 py-2 hover:bg-slate-100 flex items-center gap-2 ${isSel ? 'bg-blue-50' : ''}`}
              >
                <span className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${isSel ? 'bg-blue-600 border-blue-500' : 'border-slate-500 bg-transparent'}`}>
                  {isSel && <Check className="w-3.5 h-3.5 text-white" />}
                </span>
                <span className="text-lg">{c.icon}</span>
                <span className="text-slate-900 text-sm flex-1">{lang === 'ar' ? c.ar : c.en}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiCategorySelect;
